// ---------------------------------------------------------------------------
// mutation-client-init — single production init point for the
// cogo.ontology.* 4-tool mutation client (Phase 031 Plan 03 / D-03 / OQ1 finding).
//
// Why this module exists:
// - `createMcpMutationClient` (mcp-mutation-client.ts:117) builds the JSON-RPC
//   caller, and `useMutationStore.getState().setClient(client)` registers it
//   into the store the handler-level persistence path consumes. Both were
//   shipped in Phase 002 but never instantiated in production source —
//   `setClient` had ZERO non-test callers (RESEARCH Pitfall 2 verified
//   2026-05-17; re-verified at Plan 03 execution 2026-05-18). Every
//   `useMutationStore.addBlock(...)` call therefore threw
//   `[mutation-store] client not set — call setClient first`.
//
// Contract (the OQ1 finding's single setClient init point):
// - `initMutationClient()` is called ONCE from `installStudioBridge()` in
//   src/lib/studio-bridge.ts between `registerStudioTools()` and
//   `mcpBus.use(createValidationMiddleware())` (lines 196..203 per OQ1
//   recommendation). Idempotent: a second call is a no-op so HMR / test
//   re-installs do not double-register.
// - Production-side hard gate: only instantiates the client when
//   `isLiveAboxEnabled()` AND `deriveTenantId(session) !== null`. On the
//   default TTL path the client is never created (matching the Phase 002
//   "never instantiated in prod" baseline + the must_haves "production-side
//   mutation client only instantiates when isLiveAboxEnabled() returns true").
// - JWT supplier reads the LIVE session each call via
//   `useStudioStore.getState().session?.access_token` (T-031-08 mitigation —
//   never captures a stale token).
// - Gateway URL: env-resolved via the kg-provider.tsx:88 precedent
//   (`VITE_KG_MCP_GATEWAY ?? '<verified-live default>'`) — same env key, no
//   operator step. The 2026-05-18 live re-probe confirms the default is
//   reachable, v2.2.0 / 275 tools / 4-tool present / delete_block absent.
//
// Phase 031 Plan 05 (Codex Finding 1 / P2 — boot-race retry):
// - `subscribeAndRetry()` is the second exported side-effect installer,
//   called from `installStudioBridge()` immediately AFTER the same-tick
//   `initMutationClient()`. App.tsx runs `initAuth()` in a SEPARATE mount
//   effect from `installStudioBridge()` — so on the live boot path the
//   first `initMutationClient()` call runs BEFORE `useStudioStore.session`
//   is populated, the tenant-gate fires, and `_initialized` stays false.
//   Without this installer, the first mutation after sign-in silently
//   no-ops with `[mutation-store] client not set` (Pitfall 2 regressed
//   via boot-order race; Codex caught it post-merge).
//   `subscribeAndRetry()` installs a `useStudioStore.subscribe((s)=>s.session, …)`
//   listener that re-runs `initMutationClient()` on the null→populated
//   transition. The retry is safe because `initMutationClient()` is itself
//   idempotent + flag-gated + tenant-gated, so duplicates short-circuit.
//   See: .planning/phases/031-live-data-path/031-CODEX-REVIEW.md Finding 1.
//
// What this module DOES NOT do:
// - It does NOT wire individual handlers — that is `register-studio-tools.ts`'s
//   job (the OQ1 HANDLER classification). This module only owns the
//   single init point + the session→retry subscription.
// - It does NOT touch the @cogo/kg-browser runtime (L1b CI gate / CLAUDE.md S9
//   re-export boundary). The auth client comes from `@/lib/supabase`; the
//   mutation client only speaks JSON-RPC over HTTPS to cogo-mcp-gateway.
// ---------------------------------------------------------------------------

import { createMcpMutationClient } from './mcp-mutation-client'
import { useMutationStore } from './mutation-store'
import { isLiveAboxEnabled } from './abox-flag'
import { deriveTenantId } from '@/store/auth-slice'
import { useStudioStore } from '@/store/studio-store'
import { resolveMcpGatewayUrl } from '../mcp-gateway-url'

/**
 * Env-resolved cogo-mcp-gateway URL. Mirrors the kg-provider.tsx:88 precedent
 * verbatim (same env key, same verified-live default) so the studio's gateway
 * URL story is a single shape across kg-browser + ontology mutation clients.
 */
// null when the deployment declared it has no gateway — see
// ../mcp-gateway-url.ts.
const MCP_GATEWAY_URL = resolveMcpGatewayUrl()

/** Idempotency guard — `initMutationClient()` is called at most once per page load. */
let _initialized = false

/**
 * Subscribe-installer idempotency guard — `subscribeAndRetry()` registers the
 * session→retry listener at most once per page load. Prevents memory leaks
 * from HMR / test re-installs (Phase 031 Plan 05 / Codex Finding 1).
 */
let _subscribed = false

/**
 * Reset the idempotency guards. PRODUCTION CODE MUST NOT CALL THIS — exported
 * solely for unit tests that need a fresh init across `describe` blocks
 * (mirrors `mutation-store._resetForTest()` Phase 001 precedent).
 *
 * Resets BOTH `_initialized` (the same-tick init guard) AND `_subscribed`
 * (the subscribeAndRetry installer guard) so each test can re-exercise both
 * boundaries cleanly.
 */
export function _resetMutationClientInitForTest(): void {
  _initialized = false
  _subscribed = false
}

/**
 * Single production init point. Idempotent + flag-gated + tenant-gated.
 *
 * Sequence:
 * 1. If already initialized, return true (no-op for HMR / test re-install).
 * 2. If `isLiveAboxEnabled()` is false (default TTL path, including E2E_MOCK),
 *    return false WITHOUT creating the client — matches the Phase 002 baseline
 *    of "client never instantiated in production".
 * 3. If the current session has no resolvable tenant (`deriveTenantId === null`),
 *    return false WITHOUT creating the client. The ds-surface "Sign in to load
 *    live data" panel (Plan 02) is the operator-facing complement.
 * 4. Otherwise build the client with the env-resolved URL + per-call JWT
 *    supplier and register it into `useMutationStore`.
 *
 * @returns true if the client was created and registered in this call, false
 *          if init was skipped (already-initialized, flag-off, or no-tenant).
 */
export function initMutationClient(): boolean {
  if (_initialized) return true
  if (!isLiveAboxEnabled()) return false
  // No gateway configured (closed network) — report "not initialized" the same
  // way the other preconditions do, rather than building a client that can
  // only fail on first use.
  if (!MCP_GATEWAY_URL) return false
  const tenantId = deriveTenantId(useStudioStore.getState().session)
  if (tenantId === null) return false

  const client = createMcpMutationClient({
    url: MCP_GATEWAY_URL,
    // Read the live session per call — never capture a stale token.
    // T-031-08 mitigation: server-side RLS (Phase 417) is the authoritative
    // isolation; a stale/cross-tenant token must fail closed at the gateway.
    getAuthToken: async () => useStudioStore.getState().session?.access_token,
  })
  useMutationStore.getState().setClient(client)
  _initialized = true
  return true
}

/**
 * Install the session→retry subscription. Idempotent: subscribes at most once
 * per page load. Called once from `installStudioBridge()` immediately AFTER
 * the same-tick `initMutationClient()` call (so the synchronous-session path
 * still wins for the rare "session already populated" case, and this listener
 * handles the async case).
 *
 * Closes Codex Finding 1 (Phase 031): when `initAuth()` resolves the session
 * asynchronously (the App.tsx mount-effect ordering at src/App.tsx:101-109),
 * this listener fires and re-runs `initMutationClient()` — which is itself
 * idempotent + flag-gated + tenant-gated, so the retry is safe:
 *   - If `_initialized` is already true (e.g. session was populated at install
 *     time AND a later auth refresh fires the listener), the `if (_initialized)
 *     return true` short-circuit makes the duplicate call a no-op.
 *   - If `isLiveAboxEnabled()` is false (default TTL path), no client is created.
 *   - If `deriveTenantId(session)` returns null (partial session, anon JWT),
 *     no client is created — the listener will fire again on the NEXT session
 *     change, and the real session arrival will resolve the init.
 *
 * Listener semantics: re-run `initMutationClient()` on every session-update
 * where the new session is truthy, and delegate the decision to init's own
 * gates (`_initialized` short-circuit + flag + tenant). This covers:
 *   - boot-race recovery (null → populated): the original Codex finding case
 *   - tenant-null retry (partial session → real session): if the first session
 *     update lacks a resolvable tenant (e.g. anon JWT, user undefined), the
 *     retry waits, and the NEXT session-update that DOES resolve a tenant
 *     triggers the real init. Relying on `prevSession === null` would miss
 *     this case (must_have #4 from 031-05-PLAN.md frontmatter).
 *   - duplicate refresh (populated → populated): `_initialized` guard makes
 *     the duplicate call a cheap no-op (must_have #2).
 *   - sign-out (populated → null): out-of-scope for Plan 05; the listener
 *     no-ops because `session` is falsy. Cleanup is a future-phase concern;
 *     the client just stops being usable at the gateway when the JWT
 *     expires, which is correct fail-closed behaviour.
 *
 * @see ../../../../.planning/phases/031-live-data-path/031-CODEX-REVIEW.md Finding 1
 */
export function subscribeAndRetry(): void {
  if (_subscribed) return
  _subscribed = true
  useStudioStore.subscribe(
    (s) => s.session,
    (session) => {
      if (session) {
        initMutationClient()
      }
    },
  )
}
